import React, { useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import SalesChart from "../../components/Admin/SalesChart";
import StatCard from "../../components/Admin/StatsCard";
import DonutChartCard from "../../components/Admin/DonutChartCard";
import InventoryStatusCard from "../../components/Admin/InventoryStatusCard";
import {
  CheckCircle,
  Clock3,
  CreditCard,
  ReceiptText,
  ShoppingBag,
  Users,
  WalletCards,
  XCircle,
  AlertTriangle,
  MapPin,
  Tag,
  UserCheck,
  TrendingUp,
  Crown,
  Package,
  RefreshCcw,
  CircleDollarSign,
  ArrowDownCircle,
  ArrowUpCircle,
  ShieldAlert,
} from "lucide-react";

import {
  useDashboardDataQuery,
  useDashboardCustomerAnalyticsQuery,
  useDashboardLowStockQuery,
  useDashboardOrderStatusAnalyticsQuery,
} from "../../redux/actions/Admin/dashboardAdmin";

import { IDashboardFilterRequest } from "../../types/Request/AdminRequest";

type DashboardFilterMode = "day" | "week" | "month" | "custom";

type DashboardContextType = {
  filterMode: "day" | "week" | "month" | "custom";

  headerDates?: {
    fromDate?: string;
    toDate?: string;
  };
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { filterMode, headerDates } = useOutletContext<DashboardContextType>();
  const viewMode = filterMode === "custom" ? "week" : filterMode;
  // ============================================================
  // DASHBOARD FILTER
  //
  // Predefined periods:
  //   Today   -> backend calculates today's UAE range
  //   Weekly  -> backend calculates current UAE week
  //   Monthly -> backend calculates current UAE month
  //
  // Custom date range:
  //   Send period=Custom with startDate/endDate.
  // ============================================================

  const dashboardFilter: IDashboardFilterRequest = useMemo(() => {
    switch (filterMode) {
      case "day":
        return {
          period: "Today",
          startDate: headerDates?.fromDate,
          endDate: headerDates?.toDate,
        };

      case "month":
        return {
          period: "Monthly",
          startDate: headerDates?.fromDate,
          endDate: headerDates?.toDate,
        };

      case "custom":
        return {
          period: "Custom",
          startDate: headerDates?.fromDate,
          endDate: headerDates?.toDate,
        };

      case "week":
      default:
        return {
          period: "Weekly",
          startDate: headerDates?.fromDate,
          endDate: headerDates?.toDate,
        };
    }
  }, [filterMode, headerDates?.fromDate, headerDates?.toDate]);
  // ============================================================
  // MAIN DASHBOARD
  // ============================================================

  const { data } = useDashboardDataQuery(dashboardFilter);

  const { data: customerData } =
    useDashboardCustomerAnalyticsQuery(dashboardFilter);

  const { data: lowStockData } = useDashboardLowStockQuery(dashboardFilter);

  const { data: orderStatusData } =
    useDashboardOrderStatusAnalyticsQuery(dashboardFilter);

  // ============================================================
  // TOP STAT CARDS
  // ============================================================
  console.log("DASHBOARD FILTER:", dashboardFilter);
  const statsList = [
    {
      title: "Sales Orders",
      value: data?.summary?.totalOrders?.toLocaleString() ?? "0",
      icon: ShoppingBag,
      iconBg: "bg-blue-50 text-blue-600",
    },

    {
      title: "Total Sales",
      value: data?.summary?.totalRevenue
        ? `AED ${data.summary.totalRevenue.toLocaleString()}`
        : "AED 0",
      icon: ReceiptText,
      iconBg: "bg-emerald-50 text-emerald-600",
    },

    {
      title: "Total Profit",
      value: data?.summary?.totalProfit
        ? `AED ${data.summary.totalProfit.toLocaleString()}`
        : "AED 0",
      icon: WalletCards,
      iconBg: "bg-teal-50 text-teal-600",
    },

    {
      title: "Customers",
      value: data?.summary?.totalCustomers?.toLocaleString() ?? "0",
      icon: Users,
      iconBg: "bg-violet-50 text-violet-600",
    },

    {
      title: "Delivered Orders",
      value: data?.summary?.completedOrders?.toLocaleString() ?? "0",
      icon: CheckCircle,
      iconBg: "bg-emerald-50 text-emerald-600",
    },

    {
      title: "Pending Orders",
      value: data?.summary?.pendingOrders?.toLocaleString() ?? "0",
      icon: Clock3,
      iconBg: "bg-amber-50 text-amber-600",
    },

    {
      title: "Cancelled Orders",
      value: data?.summary?.cancelledOrders?.toLocaleString() ?? "0",
      icon: XCircle,
      iconBg: "bg-rose-50 text-rose-600",
    },

    {
      title: "Unpaid Payments",
      value: data?.summary?.pendingPayments?.toLocaleString() ?? "0",
      icon: CreditCard,
      iconBg: "bg-sky-50 text-sky-600",
    },
  ];

  // ============================================================
  // PAYMENT DONUT
  // ============================================================

  const paymentDonutData = [
    {
      label: "Paid",
      value: data?.paymentAnalytics?.paidPayments ?? 0,
      color: "#10B981",
    },

    {
      label: "Pending",
      value: data?.paymentAnalytics?.pendingPayments ?? 0,
      color: "#F59E0B",
    },

    {
      label: "Failed",
      value: data?.paymentAnalytics?.failedPayments ?? 0,
      color: "#EF4444",
    },

    {
      label: "Refunded",
      value: data?.paymentAnalytics?.refundedPayments ?? 0,
      color: "#8B5CF6",
    },
  ];

  // ============================================================
  // LOW STOCK
  // ============================================================

  const lowStockList = Array.isArray(lowStockData)
    ? lowStockData
    : lowStockData
      ? [lowStockData]
      : [];

  // ============================================================
  // FINANCIAL / REFUND ANALYTICS
  // ============================================================

  const financialAnalytics = data?.financialAnalytics;

  const grossRevenue = financialAnalytics?.grossRevenue ?? 0;

  const refundedAmount = financialAnalytics?.refundedAmount ?? 0;

  const netRevenue = financialAnalytics?.netRevenue ?? 0;

  const totalRefundIssued =
    financialAnalytics?.totalRefundIssued ?? refundedAmount;

  const extraRefundAmount = financialAnalytics?.extraRefundAmount ?? 0;

  const refundTransactionCount =
    financialAnalytics?.refundTransactionCount ?? 0;

  const fullyRefundedOrders = financialAnalytics?.fullyRefundedOrders ?? 0;

  const partiallyRefundedOrders =
    financialAnalytics?.partiallyRefundedOrders ?? 0;

  const ordersWithExtraRefund = financialAnalytics?.ordersWithExtraRefund ?? 0;

  const hasExtraRefund = extraRefundAmount > 0 || ordersWithExtraRefund > 0;

  // ============================================================
  // HELPERS
  // ============================================================

  const formatAED = (amount: number) =>
    `AED ${amount.toLocaleString("en-AE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleStockNavigation = (id: number) => {
    console.log(id, "any");
    navigate(`/admin/product/productmanagement/producteditscreen/${id}`);
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="px-1 space-y-6 pb-20">
      {/* ======================================================
          1. TOP STATS CARDS
      ====================================================== */}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsList.map((item, index) => (
          <StatCard key={index} {...item} />
        ))}
      </div>

      {/* ======================================================
          2. SALES REVENUE & PAYMENT SPLIT
      ====================================================== */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* SALES */}
        <div className="lg:col-span-2">
          <SalesChart
            viewMode={viewMode}
            title="Sales Overview"
            subtitle={
              viewMode === "day"
                ? "Today's sales by hour"
                : viewMode === "month"
                  ? "This month's sales by week"
                  : "This week's daily sales"
            }
            data={
              data?.revenueChart?.map((item) => ({
                label: item.label,
                revenue: item.revenue,
              })) ?? []
            }
          />
        </div>

        {/* PAYMENT */}
        <div className="lg:col-span-1">
          <DonutChartCard
            title="Payment Status"
            totalValue={String(data?.paymentAnalytics?.totalPayments ?? 0)}
            data={paymentDonutData}
          />
        </div>
      </div>

      {/* ======================================================
          3. FINANCIAL & REFUND ANALYTICS
      ====================================================== */}

      <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-violet-50 text-violet-600">
              <RefreshCcw className="w-5 h-5" />
            </span>

            <div>
              <h2 className="text-base font-bold text-gray-800">
                Financial & Refund Analytics
              </h2>

              <p className="text-[11px] text-gray-400 mt-0.5">
                Refund activity for the selected period
              </p>
            </div>
          </div>

          {hasExtraRefund && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100 text-xs font-bold">
              <ShieldAlert className="w-3.5 h-3.5" />
              Extra refund detected
            </span>
          )}
        </div>

        {/* FINANCIAL SUMMARY */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {/* GROSS */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600">
                <ArrowUpCircle className="w-4 h-4" />
              </span>

              <p className="text-[11px] font-semibold text-gray-500">
                Gross Revenue
              </p>
            </div>

            <p className="text-lg font-bold text-gray-800">
              {formatAED(grossRevenue)}
            </p>
          </div>

          {/* REFUNDED */}
          <div className="rounded-xl bg-violet-50/50 border border-violet-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="p-1.5 rounded-lg bg-violet-100 text-violet-600">
                <RefreshCcw className="w-4 h-4" />
              </span>

              <p className="text-[11px] font-semibold text-violet-700">
                Refunded Amount
              </p>
            </div>

            <p className="text-lg font-bold text-violet-700">
              {formatAED(refundedAmount)}
            </p>
          </div>

          {/* NET */}
          <div className="rounded-xl bg-blue-50/50 border border-blue-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="p-1.5 rounded-lg bg-blue-100 text-blue-600">
                <CircleDollarSign className="w-4 h-4" />
              </span>

              <p className="text-[11px] font-semibold text-blue-700">
                Net Revenue
              </p>
            </div>

            <p className="text-lg font-bold text-blue-700">
              {formatAED(netRevenue)}
            </p>
          </div>
        </div>

        {/* REFUND DETAILS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* TRANSACTIONS */}
          <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
            <p className="text-[11px] font-medium text-gray-500">
              Refund Transactions
            </p>

            <p className="text-xl font-bold text-gray-800 mt-1">
              {refundTransactionCount.toLocaleString()}
            </p>

            <p className="text-[10px] text-gray-400 mt-1">Completed refunds</p>
          </div>

          {/* PARTIAL */}
          <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4">
            <p className="text-[11px] font-medium text-amber-700">
              Partial Refunds
            </p>

            <p className="text-xl font-bold text-amber-700 mt-1">
              {partiallyRefundedOrders.toLocaleString()}
            </p>

            <p className="text-[10px] text-amber-600 mt-1">
              Orders partially refunded
            </p>
          </div>

          {/* FULL */}
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
            <p className="text-[11px] font-medium text-emerald-700">
              Fully Refunded
            </p>

            <p className="text-xl font-bold text-emerald-700 mt-1">
              {fullyRefundedOrders.toLocaleString()}
            </p>

            <p className="text-[10px] text-emerald-600 mt-1">
              Orders fully refunded
            </p>
          </div>

          {/* TOTAL ISSUED */}
          <div className="rounded-xl border border-gray-100 bg-white p-4">
            <p className="text-[11px] font-medium text-gray-500">
              Total Refund Issued
            </p>

            <p className="text-xl font-bold text-gray-800 mt-1">
              {formatAED(totalRefundIssued)}
            </p>

            <p className="text-[10px] text-gray-400 mt-1">
              Across completed refunds
            </p>
          </div>
        </div>

        {/* EXTRA REFUND */}
        <div
          className={`mt-4 rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
            hasExtraRefund
              ? "border-rose-200 bg-rose-50"
              : "border-gray-100 bg-gray-50/70"
          }`}
        >
          <div className="flex items-start gap-3">
            <span
              className={`p-2 rounded-xl ${
                hasExtraRefund
                  ? "bg-rose-100 text-rose-600"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              <ShieldAlert className="w-5 h-5" />
            </span>

            <div>
              <p
                className={`text-sm font-bold ${
                  hasExtraRefund ? "text-rose-700" : "text-gray-700"
                }`}
              >
                Admin Extra Refund
              </p>

              <p
                className={`text-[11px] mt-0.5 ${
                  hasExtraRefund ? "text-rose-600" : "text-gray-400"
                }`}
              >
                Refund amount issued above the original order total
              </p>
            </div>
          </div>

          <div className="text-left sm:text-right">
            <p
              className={`text-lg font-bold ${
                hasExtraRefund ? "text-rose-700" : "text-gray-800"
              }`}
            >
              {formatAED(extraRefundAmount)}
            </p>

            <p
              className={`text-[10px] mt-0.5 ${
                hasExtraRefund ? "text-rose-600" : "text-gray-400"
              }`}
            >
              {ordersWithExtraRefund} order
              {ordersWithExtraRefund === 1 ? "" : "s"} with extra refund
            </p>
          </div>
        </div>
      </div>

      {/* ======================================================
          4. CUSTOMER DATA, LOCATIONS & ORDER FLOW
      ====================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* CUSTOMERS CARD */}
        {/* CUSTOMERS CARD */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex flex-col">
          {/* HEADER */}
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 rounded-xl bg-violet-100 text-violet-600">
              <UserCheck className="w-5 h-5" />
            </div>

            <div>
              <h2 className="text-base font-bold text-gray-800">
                Customer Overview
              </h2>
              <p className="text-[11px] text-gray-400 mt-0.5">
                Customer distribution
              </p>
            </div>
          </div>

          {/* TOTAL / NEW / RETURNING */}
          <div className="grid grid-cols-3 gap-2.5 mb-5">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-100 p-3 rounded-xl text-center">
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                Total
              </p>

              <p className="text-xl font-extrabold text-gray-800 mt-1">
                {customerData?.totalCustomers ??
                  data?.summary?.totalCustomers ??
                  0}
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/70 border border-emerald-100 p-3 rounded-xl text-center">
              <p className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">
                New
              </p>

              <p className="text-xl font-extrabold text-emerald-700 mt-1">
                {customerData?.newCustomers ?? 0}
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100/70 border border-blue-100 p-3 rounded-xl text-center">
              <p className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">
                Returning
              </p>

              <p className="text-xl font-extrabold text-blue-700 mt-1">
                {customerData?.repeatCustomers ?? 0}
              </p>
            </div>
          </div>

          {/* CUSTOMER TYPES */}
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
              Customer Types
            </p>

            <span className="text-[10px] text-gray-400 font-medium">
              {customerData?.customerTags?.length ?? 0} types
            </span>
          </div>

          <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
            {customerData?.customerTags?.map((tag, idx) => {
              const tagName = (tag.name || tag.tag || "").toLowerCase();

              const styles =
                tagName === "new"
                  ? {
                      row: "bg-emerald-50 border-emerald-100 hover:bg-emerald-100",
                      icon: "bg-emerald-100 text-emerald-600",
                      text: "text-emerald-800",
                      count: "bg-emerald-100 text-emerald-700",
                    }
                  : tagName === "bronz" || tagName === "bronze"
                    ? {
                        row: "bg-amber-50 border-amber-100 hover:bg-amber-100",
                        icon: "bg-amber-100 text-amber-700",
                        text: "text-amber-900",
                        count: "bg-amber-100 text-amber-800",
                      }
                    : tagName === "silver"
                      ? {
                          row: "bg-slate-100 border-slate-200 hover:bg-slate-200",
                          icon: "bg-slate-200 text-slate-600",
                          text: "text-slate-700",
                          count: "bg-slate-200 text-slate-700",
                        }
                      : tagName === "gold"
                        ? {
                            row: "bg-yellow-50 border-yellow-100 hover:bg-yellow-100",
                            icon: "bg-yellow-100 text-yellow-600",
                            text: "text-yellow-800",
                            count: "bg-yellow-100 text-yellow-700",
                          }
                        : tagName === "platinum"
                          ? {
                              row: "bg-violet-50 border-violet-100 hover:bg-violet-100",
                              icon: "bg-violet-100 text-violet-600",
                              text: "text-violet-800",
                              count: "bg-violet-100 text-violet-700",
                            }
                          : {
                              row: "bg-gray-50 border-gray-100 hover:bg-gray-100",
                              icon: "bg-gray-100 text-gray-500",
                              text: "text-gray-700",
                              count: "bg-gray-100 text-gray-700",
                            };

              return (
                <div
                  key={idx}
                  className={`flex items-center justify-between py-2 px-2.5 rounded-xl border transition-all duration-200 ${styles.row}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${styles.icon}`}
                    >
                      <Tag className="w-3.5 h-3.5" />
                    </span>

                    <span
                      className={`text-xs font-semibold truncate ${styles.text}`}
                    >
                      {tag.name || tag.tag}
                    </span>
                  </div>

                  <span
                    className={`min-w-[28px] text-center px-2 py-1 rounded-full text-[11px] font-extrabold ${styles.count}`}
                  >
                    {tag.customerCount}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* EMIRATES */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <span className="p-2 rounded-xl bg-rose-50 text-rose-600">
              <MapPin className="w-5 h-5" />
            </span>

            <h2 className="text-base font-bold text-gray-800">
              Sales by Emirate
            </h2>
          </div>

          <div className="space-y-2 max-h-[230px] overflow-y-auto pr-1">
            {customerData?.emirateBreakdown?.length ? (
              customerData.emirateBreakdown.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50/80 hover:bg-gray-100/70 transition-colors"
                >
                  <div>
                    <p className="text-xs font-bold text-gray-800">
                      {item.emirate}
                    </p>

                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {item.orderCount} Orders · {item.customerCount} Buyers
                    </p>
                  </div>

                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-100">
                    AED {item.revenue.toLocaleString()}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 text-center py-12">
                No location data
              </p>
            )}
          </div>
        </div>

        {/* ORDER PROGRESS */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-xl bg-amber-50 text-amber-600">
                <TrendingUp className="w-5 h-5" />
              </span>

              <h2 className="text-base font-bold text-gray-800">
                Order Progress
              </h2>
            </div>

            <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {orderStatusData?.totalOrders ?? data?.summary?.totalOrders ?? 0}{" "}
              Orders
            </span>
          </div>

          <div className="space-y-3.5 max-h-[230px] overflow-y-auto pr-1">
            {orderStatusData?.statuses?.length ? (
              orderStatusData.statuses.map((status, idx) => (
                <div key={idx} className="space-y-1.5">
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-gray-700">
                      {status.name || status.status}
                    </span>

                    <span className="text-gray-900 font-bold">
                      {status.orderCount} ({status.percentage}%)
                    </span>
                  </div>

                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(status.percentage, 100)}%`,
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 text-center py-12">
                No status records
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ======================================================
          5. INVENTORY & TOP CUSTOMERS
      ====================================================== */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* INVENTORY */}
        <div className="lg:col-span-1">
          <InventoryStatusCard
            title="Stock Overview"
            data={data?.inventoryStatus}
          />
        </div>

        {/* TOP BUYERS */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <span className="p-2 rounded-xl bg-amber-50 text-amber-600">
              <Crown className="w-5 h-5" />
            </span>

            <h2 className="text-base font-bold text-gray-800">Top Buyers</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[220px] overflow-y-auto pr-1">
            {customerData?.topCustomers?.length ? (
              customerData.topCustomers.map((cust, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0 pr-2">
                    <p className="text-xs font-bold text-gray-800 truncate">
                      {cust.customerName}
                    </p>

                    <p className="text-[11px] text-gray-500">
                      {cust.orderCount} Orders · {cust.tagName || cust.tag}
                    </p>
                  </div>

                  <span className="text-xs font-bold text-emerald-600 whitespace-nowrap">
                    AED {cust.totalSpend.toLocaleString()}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 text-center py-8 col-span-2">
                No customer spend data yet
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ======================================================
          6. TOP SELLING & LOW STOCK ALERTS
      ====================================================== */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* BEST SELLERS */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <span className="p-2 rounded-xl bg-blue-50 text-blue-600">
              <Package className="w-5 h-5" />
            </span>

            <h2 className="text-base font-bold text-gray-800">
              Best Selling Items
            </h2>
          </div>

          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
            {data?.topSellingProducts?.length ? (
              data.topSellingProducts.map((item, index) => (
                <div
                  key={item.productVariantId ?? index}
                  className="flex items-center justify-between gap-3 border-b border-gray-50 pb-2.5 last:border-0"
                >
                  <div className="min-w-0">
                    <h3 className="font-semibold text-xs text-gray-800 truncate">
                      {item.productName}
                    </h3>

                    {item.variantName && (
                      <p className="text-[11px] text-gray-400">
                        {item.variantName}
                      </p>
                    )}
                  </div>

                  <div className="text-right">
                    <h4 className="font-bold text-xs text-gray-900 whitespace-nowrap">
                      AED {item.totalRevenue.toLocaleString()}
                    </h4>

                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold mt-0.5 bg-blue-50 text-blue-700">
                      {item.totalQuantitySold} sold
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-12 text-center text-xs text-gray-400">
                No items sold yet
              </p>
            )}
          </div>
        </div>

        {/* LOW STOCK */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-xl bg-rose-50 text-rose-600">
                <AlertTriangle className="w-5 h-5" />
              </span>

              <h2 className="text-base font-bold text-gray-800">
                Low Stock Alert
              </h2>
            </div>

            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-100">
              {lowStockList.length} items left
            </span>
          </div>
          <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
            {lowStockList.length > 0 ? (
              lowStockList.map((item, index) => (
                <button
                  key={item.productVariantId ?? index}
                  type="button"
                  onClick={() => handleStockNavigation(item.productId)}
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-rose-100 bg-rose-50/30 text-left cursor-pointer hover:bg-rose-50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-gray-800 truncate">
                      {item.productName}
                    </p>

                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {item.variantName ? `${item.variantName} · ` : ""}
                      AED {item.salePrice ?? item.price}
                    </p>
                  </div>

                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-700">
                    {item.currentStock} in stock
                  </span>
                </button>
              ))
            ) : (
              <p className="py-12 text-center text-xs text-gray-400">
                All stock levels look good!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
